// Session 관련 redux 액션 (Phase 2)
// backend Phase 1 의 /sql-developer/sessions API 호출.

import axios from 'axios';
import { SESSIONS_SET, SESSIONS_ERROR, SESSION_OPTIMISTIC_REMOVE, SESSION_CREATED } from '../reducers/sessionReducer';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '/sql-developer').replace(/\/+$/, '');

// session 전용 axios 인스턴스 — 인증 인터셉터 무관 (sessionId 가 path 에 있어 명시 식별).
const sessionAxios = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
    timeout: 10000,
});

// 폴링 — useSessionPolling hook 에서 직접 호출 (action 우회).
// 그러나 다른 곳에서도 1회성 새로고침이 필요할 수 있어 thunk 도 제공.
export const fetchSessions = () => async (dispatch) => {
    try {
        const res = await sessionAxios.get('/sessions');
        const list = Array.isArray(res.data) ? res.data : [];
        dispatch({ type: SESSIONS_SET, payload: list });
        return list;
    } catch (err) {
        dispatch({ type: SESSIONS_ERROR, payload: err.message });
        throw err;
    }
};

// 새 세션 생성. 다음 폴링 tick 에 list 에 자동 등장 — optimistic add 생략.
// 호출자는 반환된 sessionId 로 후속 작업(에디터 바인딩 등) 가능 — Phase 3 에서 활용.
export const createSession = (connectionId, category = 'user', label = null) => async (dispatch) => {
    if (!connectionId) throw new Error('connectionId is required');
    const res = await sessionAxios.post('/sessions', { connectionId, category, label });
    // 응답이 곧 목록의 한 줄(toJSON)이다 — 폴링을 기다리지 않고 바로 보인다
    dispatch({ type: SESSION_CREATED, payload: res.data });
    return res.data;
};

// 세션 명시 종료. 응답 전에 optimistic remove → UI 즉시 반영. 다음 폴링이 서버 상태와 동기화.
export const closeSession = (sessionId) => async (dispatch) => {
    if (!sessionId) throw new Error('sessionId is required');
    dispatch({ type: SESSION_OPTIMISTIC_REMOVE, payload: sessionId });
    try {
        await sessionAxios.delete(`/sessions/${encodeURIComponent(sessionId)}`);
    } catch (err) {
        // 실패 시 다음 폴링이 서버 상태로 복구 — 즉시 알림은 후속 phase 토스트로
        console.warn(`[sessionActions] close 실패 ${sessionId}:`, err.message);
        throw err;
    }
};

// 진행중 SQL 취소 요청 — Oracle 만 v1. 응답에 supported/attempted/forced/reason 포함.
// breakTimeoutMs: connection.break() 응답 한도 (기본 backend 30s). 초과 시 force close.
export const cancelSession = (sessionId, { breakTimeoutMs } = {}) => async () => {
    if (!sessionId) throw new Error('sessionId is required');
    const body = (breakTimeoutMs != null) ? { breakTimeoutMs } : {};
    const res = await sessionAxios.post(`/sessions/${encodeURIComponent(sessionId)}/cancel`, body);
    return res.data;
};

// 세션의 DB 측 ground truth 조회 (Oracle V$SESSION). backend 가 fresh ephemeral 세션으로 조회.
// 응답: { found, supported, alive, dbSid, dbSerial, status, state, event, waitClass, sqlId,
//        lastCallEt, secondsInWait, fallback?, reason?, error? }
export const verifySession = (sessionId) => async () => {
    if (!sessionId) throw new Error('sessionId is required');
    const res = await sessionAxios.post(`/sessions/${encodeURIComponent(sessionId)}/verify`);
    return res.data;
};
